import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import { leadsListQueryOptions } from 'app/features/leads/queries/leads-list-query-options';
import { LeadsQueryProvider } from 'app/features/leads/providers/leads-query-provider';
import GenericPage from 'app/ui/components/layouts/generic-page';
import Typography from 'app/ui/components/atoms/typography';
import PageTitle from 'app/ui/components/molecules/page-title';
import Loading from 'app/ui/components/molecules/loading';
import ErrorComponent from 'app/ui/components/molecules/error';
import { trackEvent } from 'app/utils/analytics/amplitude/tracker';

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2rem 0 1.5rem;
`;

const LeadList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(3)};
`;

const LeadListItem = styled.li`
  padding: ${({ theme: { spacing } }) => spacing(3)};
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  background: ${({ theme: { colors } }) => colors.veryGray};
`;

const LeadsPageContent = () => {
  const intl = useIntl();
  const pageTitle = intl.formatMessage({ id: 'leads.pageTitle' });

  const { data, isPending, isError } = useQuery(leadsListQueryOptions({ status: 'all', search: '' }));

  useEffect(() => {
    trackEvent('leads_page_viewed', {});
  }, []);

  if (isPending) {
    return <Loading />;
  }

  if (isError) {
    return <ErrorComponent />;
  }

  const leadCountLabel = intl.formatMessage({ id: 'leads.count' }, { count: data.totalCount });

  return (
    <GenericPage pageTitle={pageTitle}>
      <HeaderRow>
        <PageTitle>
          <Typography variant="h3">{pageTitle}</Typography>
        </PageTitle>
      </HeaderRow>
      <Typography variant="body2">{leadCountLabel}</Typography>
      <LeadList>
        {data.leads.map((lead) => (
          <LeadListItem key={lead.id}>
            <Typography variant="body1">{lead.athleteName}</Typography>
            <Typography variant="caption">{lead.athleteEmail}</Typography>
          </LeadListItem>
        ))}
      </LeadList>
    </GenericPage>
  );
};

const LeadsPage = () => (
  <LeadsQueryProvider>
    <LeadsPageContent />
  </LeadsQueryProvider>
);

export default LeadsPage;
